import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { Breadcrumbs, PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CategoryNavigation } from "@/components/menu/CategoryNavigation";
import { ProductCard } from "@/components/menu/ProductCard";
import { EmptyState, ErrorState, ProductSkeletonList } from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { branchesQuery, categoriesQuery, menuItemsQuery } from "@/lib/queries";
import { formatHours, loc } from "@/lib/format";
import { activeMenuCategory } from "@/lib/menu-model";

export const Route = createFileRoute("/branchMenu/$branchId")({
  component: BranchMenu,
});

function BranchMenu() {
  const { branchId } = Route.useParams();
  const { t, lang } = useI18n();
  const [categoryId, setCategoryId] = useState<string>("");
  const [term, setTerm] = useState("");

  const branches = useQuery(branchesQuery());
  const categories = useQuery(categoriesQuery(branchId, "indoor"));
  const branch = branches.data?.find((b) => b.id === branchId);
  const activeCategory = activeMenuCategory(categories.data ?? [], categoryId);

  const items = useQuery({
    ...menuItemsQuery({
      branchId,
      ...(!term && activeCategory ? { categoryId: activeCategory } : {}),
      orderType: "indoor",
      ...(term ? { name: term } : {}),
      limit: 50,
    }),
    enabled: Boolean(term) || Boolean(activeCategory),
  });

  const list = useMemo(() => items.data?.data ?? [], [items.data]);

  return (
    <PageShell>
      <Breadcrumbs
        items={[
          { label: t("nav.home"), to: "/" },
          { label: t("branchMenu.selectTitle"), to: "/branchMenu" },
          { label: branch ? loc(branch.name, lang) : t("branchMenu.selectTitle") },
        ]}
      />
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black sm:text-4xl">
            {branch ? loc(branch.name, lang) : t("branchMenu.selectTitle")}
          </h1>
          {branch ? (
            <p className="mt-1 text-muted-foreground">
              {loc(branch.address, lang)} · {formatHours(branch.openingHours, lang)}
            </p>
          ) : null}
        </div>
        <Button asChild variant="outline">
          <Link to="/branchMenu">
            <ArrowRight className="size-4 ltr:rotate-180" />
            {t("common.change")}
          </Link>
        </Button>
      </div>
      <Input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder={t("branchMenu.searchPlaceholder")}
        aria-label={t("branchMenu.searchPlaceholder")}
        className="mb-6 h-12 max-w-md rounded-2xl"
      />

      <div className="grid min-w-0 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <CategoryNavigation
          categories={categories.data ?? []}
          activeCategoryId={activeCategory}
          searching={Boolean(term.trim())}
          onSelect={(id) => {
            setTerm("");
            setCategoryId(id);
          }}
        />

        <div className="grid min-w-0 content-start gap-4">
          {categories.isError ? (
            <ErrorState
              error={categories.error}
              onRetry={() => categories.refetch()}
            />
          ) : categories.isPending || items.isPending ? (
            <ProductSkeletonList />
          ) : items.isError ? (
            <ErrorState error={items.error} onRetry={() => items.refetch()} />
          ) : list.length === 0 ? (
            <EmptyState title={term ? t("menu.noResults") : t("menu.emptyCategory")} />
          ) : (
            list.map((item) => <ProductCard key={item.id} item={item} mode="indoor" />)
          )}
        </div>
      </div>
    </PageShell>
  );
}
